import { CRAYO_ENTRY, crayoPlan, formatHours, money, crayoPlanTerms } from "@/lib/compare/crayo";
import type { CompareContent } from "@/lib/compare/types";

const hobby = CRAYO_ENTRY;
const clipper = crayoPlan("Clipper");
const PRICING = "https://www.opus.pro/pricing";

export const OPUS_CLIP_CONTENT: CompareContent = {
  slug: "crayo-vs-opus-clip",
  competitorSlug: "opus-clip",
  title: "Crayo vs Opus Clip",
  seoTitle: "Crayo vs Opus Clip: Pricing, Metering and Features Compared",
  seoDescription:
    "Crayo vs Opus Clip, with dated prices. Opus Clip charges 1 credit per minute uploaded. Crayo charges 1 credit per finished clip, ranks moments before you cut and puts a timeline editor on every plan.",
  cardVerdict: "Better than Crayo for free tier and scheduling, behind on metering and languages.",
  bestFor: "Clippers who upload more than they keep",
  datePublished: "2026-10-09",
  dateModified: "2026-10-25",
  verdict: `As of October 25, 2026, Crayo is the better pick if you clip long podcasts and streams and keep fewer clips than minutes you upload: it charges per finished clip, ranks moments before you cut, and puts a timeline editor and 56 caption languages on one ${money(hobby.monthly)} plan. Opus Clip is the better pick if you need a free tier, a scheduler to six networks, or team seats. The evidence is below.`,
  pickCrayoIf: [
    "You pay per finished clip, not per minute uploaded: a 45-minute interview that yields 2 clips costs 2 credits, not 45.",
    "You want to see ranked moments before spending anything: Find Viral Moments lists up to 50, and a credit is spent only when you cut.",
    "You want a timeline editor with a face-aware camera track, 56 caption languages and a public API on the entry plan, with no watermarked tier and no clips that expire.",
  ],
  pickThemIf: [
    "You need a free tier or a 7-day trial before paying. Opus Clip gives 60 credits a month free; Crayo has neither.",
    "You schedule posts to six networks from the clipping tool. Opus Clip Pro includes the scheduler; Crayo has none.",
    "You work in a team: Pro includes 2 seats, Business adds SSO and unlimited seats. Crayo accounts are single-seat.",
  ],
  wins: [
    {
      claim: "Crayo meters by finished clip; Opus Clip meters by source minute.",
      evidence:
        "Opus Clip's pricing page, checked Oct 8, 2026, defines 1 credit as 1 minute of uploaded video, so a 60-minute podcast costs 60 credits however many clips you keep, and Starter's 150 credits are gone after two and a half hours of source. Crayo charges 1 workflow credit per clip you generate, and Clip Cutter takes manual ranges at no credit cost. Find Viral Moments lists up to 50 ranked moments to review before any credit is spent.",
      href: PRICING,
    },
    {
      claim: "Crayo transcribes 56 spoken languages; Opus Clip lists 20+.",
      evidence:
        "The Opus Clip pricing page says AI animated captions in 20+ languages, and the homepage names English, German, Spanish, French and Portuguese among them. Crayo's language picker has 56 entries, all transcribed with the same model, with 32 caption styles and keyword highlight on every plan.",
      href: PRICING,
    },
    {
      claim: "Crayo's timeline editor is on every plan; Opus Clip's Free plan has no editing and its clips expire.",
      evidence:
        "The Opus Clip pricing page lists No editing on Free, clips that stop being exportable after 3 days, and media that expires after 29 days on Starter. Crayo's editor, with a camera track, keyframes, zooms, transitions and speed ramps, opens every AutoClip clip on Hobby and up, and nothing expires while the plan is active.",
      href: PRICING,
    },
    {
      claim: "Crayo cancels in one click from Settings; Opus Clip does not describe cancellation on its pricing page.",
      evidence:
        "Opus Clip's pricing page, checked Oct 8, 2026, carries no refund or cancellation terms. Crayo's refund policy states the terms in full: cancel from Settings, the plan stays active to the end of the cycle, nothing further is charged. One dated Trustpilot review about cancelling Opus Clip is linked in the billing section below.",
      href: "https://crayo.ai/refund-policy",
    },
  ],
  concessions: [
    {
      claim: "Opus Clip has a real free tier plus a 7-day Pro trial.",
      evidence:
        "The Free plan gives 60 credits a month, with a watermark, no editing and 3-day clip expiry, and signup comes with a 7-day Pro trial of 90 credits, per the pricing page on Oct 8, 2026. Crayo has no free plan and no trial; the only free Crayo products are twelve browser tools.",
      href: PRICING,
    },
    {
      claim: "Opus Clip schedules posts to six networks and has a mature API with an MCP server, SDKs and Zapier.",
      evidence:
        "Pro includes the Scheduler API and 6 social connections, adding LinkedIn, Facebook and X to the YouTube, TikTok and Instagram posting on Starter. The API page lists 6 SDKs, an OpenAPI spec and an MCP connector. Crayo's API has 34 endpoints and webhooks but no MCP server, and Crayo has no scheduler at all.",
      href: "https://www.opus.pro/api",
    },
    {
      claim: "Opus Clip has team workspaces and a far bigger footprint.",
      evidence:
        "Pro includes a team workspace with 2 seats, sold in packs of 2 up to 4, and Business is unlimited with SSO. The homepage claims 20M+ creators and businesses. Crayo is single-seat on every plan.",
      href: "https://www.opus.pro/",
    },
    {
      claim: "Opus Clip generates AI and stock B-roll inside the clipper.",
      evidence:
        "Starter includes 3 AI B-roll clips and 3 stock B-roll clips a month, and Pro raises AI image B-roll to 50 a day with unlimited stock B-roll, per the pricing page. Crayo lets you add your own B-roll in the editor but does not generate it.",
      href: PRICING,
    },
  ],
  typicalMonth: {
    scenario: "One 60-minute source a week, eight clips each: 240 source minutes and 32 clips a month.",
    crayo: {
      plan: "Hobby",
      cost: `${money(hobby.monthly)}/mo or about ${money(hobby.yearlyPerMonth)}/mo yearly`,
      usage: `32 of ${hobby.credits} workflow credits and about 32 of ${hobby.exportMinutes} export minutes`,
      note: "Prices from the live plan table.",
    },
    them: {
      plan: "Pro",
      cost: "$29/mo or $14.50/mo yearly ($174/yr)",
      usage: "240 of 300 credits; Starter's 150 credits would run out in the third week",
      note: "1 credit per uploaded minute, per the Opus Clip pricing page.",
    },
  },
  test: {
    status: "scheduled",
    source: "A 45-minute two-person podcast recording owned by Crayo",
    plans: "Opus Clip Starter and Crayo Clipper, same file, same day, defaults first then one tuned run each",
  },
  billingNote:
    "Opus Clip Starter is monthly-only with no rollover; Pro credits roll over for 2 months. Starter media expires after 29 days and Free clips stop being exportable after 3 days. Refund and cancellation terms are not on the pricing page, and 4K download is a Business-plan feature.",
  faqs: [
    {
      question: "Is Crayo cheaper than Opus Clip?",
      answer: `For most clippers, yes. Crayo Hobby is ${money(hobby.monthly)} a month or ${money(hobby.yearlyTotal)} a year for ${hobby.credits} credits, and one credit is one finished clip. Opus Clip Starter is $15 a month, monthly billing only, for 150 credits, and one credit is one uploaded minute, so four hour-long sources use all 150 before any clip is counted; Pro at $29 a month, or $14.50 a month billed yearly, raises that to 300 credits. If you upload short sources and keep most of what the tool finds, Opus Clip Starter can be cheaper. If you upload long sources and keep a few clips, Crayo is.`,
    },
    {
      question: "Does Crayo have a free plan like Opus Clip?",
      answer: `No. Crayo has no free plan and no trial; the only free products are twelve browser tools at crayo.ai/features/free that need no account and never upload your file. The entry plan is Hobby at ${money(hobby.monthly)} a month. Opus Clip's free plan gives 60 credits a month, which is 60 minutes of uploaded video, with a watermark, no editing, and clips that stop being exportable after 3 days, and new accounts get a 7-day Pro trial. If a free tier is the deciding factor, Opus Clip wins that row.`,
    },
    {
      question: "What does Opus Clip cost?",
      answer:
        "As of Oct 8, 2026, Opus Clip's pricing page lists Free at $0 for 60 credits a month, Starter at $15 a month for 150 credits with no annual option, Pro at $29 a month or $14.50 a month billed yearly ($174 a year) for 300 credits, and Business on request. Regional price tiers exist in the page's own script, so the figure you see may be lower outside the United States. One credit is one minute of uploaded video on every plan, Pro credits roll over for 2 months, and 4K download is reserved for Business.",
    },
    {
      question: "Does Crayo have virality scores like Opus Clip?",
      answer:
        "Crayo ranks moments rather than printing a score. AutoClip and Find Viral Moments surface the strongest moments first, with the planner's reasons attached, so you review the ranked list before cutting; Find Viral Moments shows up to 50 of them for one credit each only when you cut. Opus Clip shows a Virality Score per clip inside its ClipAnything flow. No side-by-side test has run yet; the scheduled test on a 45-minute podcast will record how many clips from each tool pass the usable rubric.",
    },
    {
      question: "Which is better for podcasts and streams, Crayo or Opus Clip?",
      answer:
        "Podcasters and streamers who upload long episodes and keep a handful of clips pay less on Crayo, because metering is per clip and a 90-minute episode costs nothing until a clip is cut. Opus Clip's per-minute metering suits creators who upload short sources often and keep most of the output. Both reframe faces and caption automatically; Crayo transcribes 56 languages to Opus Clip's 20+ and adds speaker, streamer, split and full layouts, while Opus Clip adds AI B-roll and a scheduler. The scheduled hands-on test uses a two-person podcast on both tools.",
    },
    {
      question: "Can I move from Opus Clip to Crayo?",
      answer: `Yes, and there is nothing to migrate. Paste the YouTube or TikTok link, or upload the file up to 4 GB and 3 hours, pick a layout and intensity, and export at 1080p or open the clips in the editor. Your source files stay yours on both sides. Opus Clip Starter media expires after 29 days, so download anything you want to keep before cancelling there. On Crayo, Hobby gives ${hobby.credits} credits and ${formatHours(hobby.exportMinutes)} of export a month; Clipper raises that to ${clipper.credits} credits and ${formatHours(clipper.exportMinutes)} for ${money(clipper.monthly)} a month.`,
    },
    {
      question: "Does Crayo auto-post like Opus Clip?",
      answer:
        "No. Crayo exports the file and you post it yourself. Opus Clip posts to YouTube, TikTok and Instagram on Starter and schedules to six social connections on Pro. If native scheduling is the deciding feature, Opus Clip wins that row. Crayo's View Tracker is a different tool: it follows Instagram, YouTube and TikTok creators to show posting cadence and viral outliers, and it does not publish anything.",
    },
    {
      question: "Does Crayo also make story or text videos?",
      answer:
        "Yes, as a side feature. Every Crayo plan also includes Reddit story videos, iMessage-style text videos, AI voiceover with hundreds of voices and AI images, and each finished one costs 1 credit from the same pool as the clips. Opus Clip has none of these: its AI images exist only as B-roll and its voice-over is capped at 20 a day on paid plans. This page compares the clippers, so those tools do not count in the verdict above; they matter only if you run a channel that mixes clipped footage with generated videos and want one plan for both.",
    },
  ],
  changelog: [
    {
      date: "2026-10-09",
      text: "First published. Opus Clip prices checked Oct 8, 2026: Starter $15/mo monthly-only, Pro $29/mo or $14.50/mo billed annually ($174/yr), 4K on Business only.",
    },
    {
      date: "2026-10-25",
      text: `Crayo prices updated to the October 25, 2026 ladder: Hobby is ${crayoPlanTerms(hobby)}, with quarterly the default term at checkout. Verdicts and the corrections box re-checked at the new prices; competitor prices unchanged from the October 8 to 9 check.`,
    },
  ],
  relatedSpokes: ["crayo-vs-vizard", "crayo-vs-klap", "crayo-vs-submagic"],
  featurePath: "/features/auto-clip",
  ctaHref: "https://crayo.ai/create/auto-clip",
  ctaLabel: "Clip a video now",
  targetQueries: [
    "crayo vs opus clip",
    "opus clip vs crayo ai",
    "opus clip alternative",
    "opusclip alternative",
    "opus clip pricing",
    "is opus clip worth it",
  ],
};
